import { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { REQUEST_PAYMENT_LINK_EXPIRED, REQUEST_PAYMENT_THANK_YOU, ROUTE_ADD_PAYMENT, ROUTE_REQUEST_PAYMENT } from 'src/constants';
import { unauthorizeLogout } from 'src/Features/AccountSetup/redux/actions';
import { isPaymentTokenExpired, isTokenExpired, refetchPaymentToken, refreshToken } from 'src/helper/authentication';
import { bugSnagNotifyAPIError } from 'src/helper/bugSnagHelper';
import {
  getAddPaymentUser,
  getRequestId,
  getUser,
} from 'src/helper/helperMethods';
import store from 'src/redux/store/store';

const { dispatch } = store;
let isRefreshing = false;
let refreshPromise: Promise<any> | null = null;
//Queue to manage request promises, created while the token is being fetched 
let requestPromiseQueue: ((token: any) => void)[] = [];
//Function that will resolve all the promises queued, after new token being fetched
function onRefreshed(token: any) {
  requestPromiseQueue.forEach((callback) => callback(token));
  requestPromiseQueue = [];
}
//Function to Queue request Promises which will require new refreshed tokens
function queueRefreshToken(requestPromise: (token: any) => void) {
  requestPromiseQueue.push(requestPromise);
}

const setAuthHeaders = (request: any, tokenInfo: any) => {
  request.headers['Authorization'] = `Bearer ${tokenInfo.token || tokenInfo.authentication_token}`;
  request.headers['token'] = tokenInfo.authentication_token || tokenInfo.token;
  request.headers['Refresh-token'] = tokenInfo.refresh_token || "";
};

const handleRequestPaymentFlow = async (currentPath: string, authInfo: any, request) => {
  const addPaymentUser = getAddPaymentUser();
  let shouldRefetchToken = await isPaymentTokenExpired(addPaymentUser);
  const [, requestId] = currentPath.split('/').filter(Boolean);
  authInfo = {
    authentication_token: addPaymentUser?.authentication_token,
    token: addPaymentUser?.authentication_token,
  };
  const isExcludedPath =
    currentPath.includes("link-expired") || currentPath.includes("thankYou");
  if (shouldRefetchToken && !isExcludedPath) {
    if (!isRefreshing) {
      isRefreshing = true;
      refreshPromise = refetchPaymentToken(requestId, currentPath.includes(ROUTE_ADD_PAYMENT))
        .then((tokens) => {
          isRefreshing = false;
          onRefreshed(tokens);
          return tokens;
        })
        .catch((error) => {
          isRefreshing = false;
          refreshPromise = null;
          throw error;
        });
    }
    const newUserTokens = await new Promise<any>((resolve) => {
      queueRefreshToken(resolve);
    });
    if (newUserTokens?.authentication_token) {
      setAuthHeaders(request, newUserTokens);
    }
    return { authInfo, newUserTokens };
  } else {
    return { authInfo, newUserTokens: null };
  }
};

const handleNormalTokenFlow = async (authInfo: any, request) => {
  const { shouldRefetchToken: shouldRefresh, shouldLogout } = await isTokenExpired(authInfo);
  if (shouldLogout) {
    unauthorizeLogout(dispatch);
    return { authInfo, newUserTokens: null, shouldLogout: true };
  }
  if (shouldRefresh) {
    if (!isRefreshing) {
      isRefreshing = true;
      const currentAuthUser = getUser();
      refreshPromise = refreshToken(dispatch, currentAuthUser)
        .then((newToken) => {
          isRefreshing = false;
          onRefreshed(newToken);
          return newToken;
        })
        .catch((error) => {
          isRefreshing = false;
          refreshPromise = null;
          throw error;
        });
    }
    // [IMPORTANT] - This code block will create new promise against each async request and push them in queue, 
    // they will be resolved once they get new refreshed tokens.
    const newUserTokens = await new Promise<any>((resolve) => {
      queueRefreshToken(resolve);
    });
    if (newUserTokens?.token) {
      setAuthHeaders(request, newUserTokens);
    }
    return { authInfo, newUserTokens, shouldLogout: false };
  }
  else {
    return { authInfo, newUserTokens: null, shouldLogout: false };
  }
};

export const requestHandler = async (request: AxiosRequestConfig) => {
  const currentPath = window.location.pathname;
  let authInfo = getUser();
  let newUserTokens: any = null;
  let shouldLogout = false;

  if (currentPath.includes(ROUTE_REQUEST_PAYMENT) || currentPath.includes(ROUTE_ADD_PAYMENT)) {
    ({ authInfo, newUserTokens } = await handleRequestPaymentFlow(currentPath, authInfo, request));
  } else {
    ({ authInfo, newUserTokens, shouldLogout } = await handleNormalTokenFlow(authInfo, request));
    if (shouldLogout) return request;
  }
  let requestId = getRequestId();
  // this checks if refreshed tokens are already set in request header or not 
  if (!newUserTokens) {
    request.headers['Authorization'] = request.headers['Authorization'] || 'Bearer ' + authInfo.token;
    request.headers['token'] = request.headers['token'] || authInfo.authentication_token;
    request.headers['Refresh-token'] = request.headers['Refresh-token'] || "";
  }
  request.headers['request-id'] = request.headers['request-id'] || requestId;
  return request;
};

export const successResponseHandler = (response: AxiosResponse) => {
  return response;
};

export const errorResponseHandler = (error: AxiosError) => {
  if (error?.response?.status === 401) {
    let payload = error.response.config?.data
      ? JSON.parse(error.response.config.data)
      : null;
    let apiInfo = {
      'Auth-token': error?.response?.config?.headers?.['Authorization'] || "",
      'Refresh-token': error?.response?.config?.headers?.['Refresh-token'] || "",
      'Payload': payload
    }
    unauthorizeLogout(dispatch);
    bugSnagNotifyAPIError("Authentication Error", apiInfo, `Request failed with status code 401`);
  }
  // Handles Server Errors for Bugsnag reporting
  if (error?.response?.status >= 500 && error?.response?.status < 600) {
    bugSnagNotifyAPIError("Server Error occurred", error?.response, `Server Error with status code ${error?.response?.status}`);
  }
  // Handles Network Errors for Bugsnag reporting
  if (!error?.response) {
    bugSnagNotifyAPIError("Network error occurred", error, error?.message);
  }
  return Promise.reject(error);
};